import React from 'react'
import PropTypes from 'prop-types'
import './FrameworkProgressList.scss'

/**
 * FrameworkProgressList - Compact list view for framework/project progress
 * Optimized for displaying multiple records in a scrollable list format
 *
 * @param {Array} items - Array of framework/project items with progress data
 * @param {boolean} loading - Show loading skeleton
 * @param {number} maxHeight - Maximum height before scrolling (default: 300px)
 * @param {function} onItemClick - Optional click handler for items
 */
const FrameworkProgressList = ({
	items = [],
	loading = false,
	maxHeight = 300,
	onItemClick
}) => {
	const getProgressVariant = (progress) => {
		if (progress >= 80) return 'success'
		if (progress >= 50) return 'warning'
		if (progress >= 25) return 'info'
		return 'danger'
	}

	const getStatusLabel = (status) => {
		const statusMap = {
			planning: 'Planning',
			in_progress: 'In Progress',
			review: 'Review',
			completed: 'Completed',
			on_hold: 'On Hold'
		}
		return statusMap[status] || status
	}

	const getStatusClass = (status) => {
		const classMap = {
			planning: 'planning',
			in_progress: 'in-progress',
			review: 'review',
			completed: 'completed',
			on_hold: 'on-hold'
		}
		return classMap[status] || 'default'
	}

	if (loading) {
		return (
			<div className="framework-progress-list">
				<div className="framework-progress-list__container">
					{[1, 2, 3, 4, 5].map((i) => (
						<div key={i} className="framework-progress-list__item framework-progress-list__item--skeleton">
							<div className="skeleton skeleton--title"></div>
							<div className="skeleton skeleton--bar"></div>
						</div>
					))}
				</div>
			</div>
		)
	}

	if (!items || items.length === 0) {
		return (
			<div className="framework-progress-list framework-progress-list--empty">
				<div className="framework-progress-list__empty-state">
					<span className="framework-progress-list__empty-icon">📋</span>
					<p>No active frameworks</p>
				</div>
			</div>
		)
	}

	return (
		<div className="framework-progress-list">
			<div className="framework-progress-list__header">
				<span className="framework-progress-list__count">
					{items.length} {items.length === 1 ? 'Project' : 'Projects'}
				</span>
			</div>
			<div
				className="framework-progress-list__container"
				style={{ maxHeight: `${maxHeight}px` }}
			>
				{items.map((item, index) => {
					const progress = item.progress || 0
					const variant = getProgressVariant(progress)
					const completed = item.completed || item.completed_items || 0
					const total = item.total || item.total_items || 0

					return (
						<div
							key={item.id || index}
							className={`framework-progress-list__item ${onItemClick ? 'framework-progress-list__item--clickable' : ''}`}
							onClick={() => onItemClick && onItemClick(item)}
							role={onItemClick ? 'button' : undefined}
							tabIndex={onItemClick ? 0 : undefined}
						>
							<div className="framework-progress-list__item-header">
								<h4 className="framework-progress-list__item-title" title={item.name || item.framework_name}>
									{item.name || item.framework_name}
								</h4>
								{item.status && (
									<span className={`framework-progress-list__status framework-progress-list__status--${getStatusClass(item.status)}`}>
										{getStatusLabel(item.status)}
									</span>
								)}
							</div>
							<div className="framework-progress-list__item-body">
								<div className="framework-progress-list__progress-track">
									<div
										className={`framework-progress-list__progress-fill framework-progress-list__progress-fill--${variant}`}
										style={{ width: `${progress}%` }}
									/>
								</div>
								<div className="framework-progress-list__stats">
									<span className={`framework-progress-list__percentage framework-progress-list__percentage--${variant}`}>
										{progress}%
									</span>
									{total > 0 && (
										<span className="framework-progress-list__ratio">
											{completed}/{total}
										</span>
									)}
								</div>
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)
}

FrameworkProgressList.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			name: PropTypes.string,
			framework_name: PropTypes.string,
			status: PropTypes.string,
			progress: PropTypes.number,
			completed: PropTypes.number,
			completed_items: PropTypes.number,
			total: PropTypes.number,
			total_items: PropTypes.number
		})
	),
	loading: PropTypes.bool,
	maxHeight: PropTypes.number,
	onItemClick: PropTypes.func
}

export default FrameworkProgressList
